import React, { useState } from "react";
import { PlusIcon, MinusIcon } from "@heroicons/react/24/outline";
import { useCart } from "../../contexts/CartContext";
import { Item } from "../../types";

interface ProductCardProps {
  product: Item;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [inputValue, setInputValue] = useState("1");

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    const parsedValue = parseInt(value);
    if (!isNaN(parsedValue) && parsedValue > 0) {
      setQuantity(parsedValue);
    }
  };

  const handleIncrement = () => {
    setQuantity((prev) => {
      const newValue = prev + 1;
      setInputValue(newValue.toString());
      return newValue;
    });
  };

  const handleDecrement = () => {
    setQuantity((prev) => {
      if (prev <= 1) return 1;
      const newValue = prev - 1;
      setInputValue(newValue.toString());
      return newValue;
    });
  };

  const handleInputBlur = () => {
    if (
      inputValue === "" ||
      isNaN(parseInt(inputValue)) ||
      parseInt(inputValue) < 1
    ) {
      setQuantity(1);
      setInputValue("1");
    }
  };

  const handleAddToCart = () => {
    // Use false for incremental to set the quantity exactly as specified
    addToCart(product, quantity, false);
    setQuantity(1);
    setInputValue("1");
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg h-full flex flex-col">
      {product.imageURL ? (
        <div className="w-full aspect-square overflow-hidden">
          <img
            src={product.imageURL}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="aspect-square bg-gray-200 flex items-center justify-center">
          <p className="text-gray-500 font-medium">No Image Available</p>
        </div>
      )}

      <div className="p-4 flex-grow">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {product.name}
        </h3>
        <p className="text-gold text-xl font-bold mb-2">
          ${parseFloat(product.price).toFixed(2)}
        </p>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {product.description}
        </p>

        <div className="mt-auto">
          <div className="flex items-center mb-3">
            <button
              onClick={handleDecrement}
              className="p-1 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
            >
              <MinusIcon className="h-4 w-4" />
            </button>

            <input
              type="text"
              value={inputValue}
              onChange={handleQuantityChange}
              onBlur={handleInputBlur}
              className="mx-2 w-12 text-center border border-gray-300 rounded-md p-1 text-sm"
            />

            <button
              onClick={handleIncrement}
              className="p-1 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
            >
              <PlusIcon className="h-4 w-4" />
            </button>
          </div>

          <button
            onClick={handleAddToCart}
            className="w-full py-2 px-4 bg-gold hover:bg-opacity-90 text-white rounded-md transition-colors duration-200 flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
